import { FullScreenLoading, SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui';

import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  useFetchPublicProfileInfoQuery,
  useFetchProfilePermissionsQuery,
  useFetchUserProfileInfoQuery,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';
import { USER_PROFILE_FIELDS } from '@backrs_ui/grow-ui/src/redux/profile/profile.constants';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';

import { Box, SxProps, Theme } from '@mui/material';

import { ViewPermissionDenied } from './atoms/ViewPermissionDenied';
import { ProfileLinkedIn } from './molecules/ProfileLinkedIn';
import { PublicProfileHeader } from './molecules/PublicProfileHeader';
import { PublicProfileSnapshot } from './molecules/PublicProfileSnapshot';
import { PublicProfileStory } from './molecules/PublicProfileStory';

type PublicProfileProps = Readonly<{
  userId: string;
}>;

export const PublicProfile = ({ userId }: PublicProfileProps) => {
  const loggedInUserId = useAppSelector(selectUserId);

  const { data: permissions, isLoading: isLoadingPermissions } = useFetchProfilePermissionsQuery({
    userId,
  });

  const { data: currentUserProfile } = useFetchUserProfileInfoQuery(USER_PROFILE_FIELDS, {
    skip: !userId || !permissions?.view_profile.allowed,
  });

  const { data: publicProfile, isLoading: isLoadingPublicProfile } = useFetchPublicProfileInfoQuery(
    { userId },
    { skip: !permissions?.view_profile.allowed },
  );

  const isViewingOwnProfile = loggedInUserId?.toString() === userId;
  const profileToUse = isViewingOwnProfile ? currentUserProfile : publicProfile;

  if (!permissions?.view_profile.allowed && !isLoadingPermissions) {
    return <ViewPermissionDenied message={permissions?.view_profile.message} />;
  }

  if (isLoadingPermissions || (isLoadingPublicProfile && !isViewingOwnProfile) || !profileToUse) {
    return <FullScreenLoading />;
  }

  return (
    <Box sx={pageContainerSx}>
      {/** HEADER */}
      <PublicProfileHeader publicProfile={profileToUse} loggedInUserId={loggedInUserId} />

      <Box sx={contentContainerSx}>
        {/** SNAPSHOT */}
        <PublicProfileSnapshot publicProfile={profileToUse} />

        {/** LINKEDIN */}
        {publicProfile?.profile?.linkedInData && (
          <ProfileLinkedIn
            firstName={publicProfile.profile?.publicProfile?.first_name || ''}
            linkedInData={profileToUse?.profile.linkedInData}
          />
        )}

        {/** STORY */}
        <PublicProfileStory publicProfile={profileToUse} />
      </Box>
    </Box>
  );
};

const pageContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Beige/50'],
  minHeight: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
});

const contentContainerSx: SxProps<Theme> = {
  margin: '0 auto',
  maxWidth: '1216px',
  // target chromebook screen size
  '@media (max-width: 1366px) and (max-height: 768px) and (min-width: 405px)': {
    maxWidth: `calc(100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - 16px)`, // 16px for scrollbar
    marginLeft: `${SIDEBAR_COLLAPSED_WIDTH}px`,
  },
  '@media (max-width: 900px) and (max-height: 504px)': {
    maxWidth: '100%',
    marginLeft: '0px',
  },
  width: '100%',
  overflowX: 'hidden',
};
